import { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2, MessageSquare, Search, Check, CheckCheck } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * 1:1 Manager ↔ Parent chat drawer (right-side slide-in).
 *
 * Usage:
 *   <ManagerChatDrawer
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     teamId={teamId}
 *     parent={{ id, name, email }}
 *   />
 */
const ManagerChatDrawer = ({ open, onClose, teamId, parent }) => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [parentTyping, setParentTyping] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const bottomRef = useRef(null);
  const typingPollRef = useRef(null);
  const typingDebounceRef = useRef(null);

  useEffect(() => {
    if (open && teamId && parent?.id) fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, teamId, parent?.id]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, parentTyping]);

  // Poll the typing flag while the drawer is open
  useEffect(() => {
    if (!open || !teamId || !parent?.id) {
      setParentTyping(false);
      return;
    }
    const token = localStorage.getItem('team_token');
    const poll = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/clubs/team/${teamId}/manager/chat/${parent.id}/typing`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const d = await res.json();
          setParentTyping(!!d.typing);
        }
      } catch (e) {}
    };
    poll();
    typingPollRef.current = setInterval(poll, 2500);
    return () => {
      if (typingPollRef.current) clearInterval(typingPollRef.current);
    };
  }, [open, teamId, parent?.id]);

  const pingTyping = () => {
    if (!open || !teamId || !parent?.id) return;
    if (typingDebounceRef.current) return; // throttle to ~1 ping / 1.5s
    typingDebounceRef.current = setTimeout(() => {
      typingDebounceRef.current = null;
    }, 1500);
    const token = localStorage.getItem('team_token');
    fetch(
      `${API_URL}/api/clubs/team/${teamId}/manager/chat/${parent.id}/typing`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => {});
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('team_token');
      const res = await fetch(
        `${API_URL}/api/clubs/team/${teamId}/manager/chat/${parent.id}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setMessages(await res.json());
    } catch (e) {
      // silent — chat is non-critical
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const token = localStorage.getItem('team_token');
      const res = await fetch(
        `${API_URL}/api/clubs/team/${teamId}/manager/chat/${parent.id}/messages`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ body }),
        }
      );
      if (res.ok) {
        const msg = await res.json();
        setMessages((prev) => [...prev, msg]);
        setDraft('');
      }
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="manager-chat-drawer"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        data-testid="manager-chat-backdrop"
      />

      {/* Drawer */}
      <div className="relative w-full max-w-md bg-card border-l border-border flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold">
              {(parent?.name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-medium truncate">{parent?.name || 'Parent'}</p>
              {parent?.email && (
                <p className="text-xs text-muted-foreground truncate">{parent.email}</p>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowSearch((s) => !s)}
            className="text-muted-foreground hover:text-foreground p-1.5 rounded transition mr-1"
            title="Search in conversation"
            data-testid="manager-chat-search-toggle"
          >
            <Search size={16} />
          </button>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1.5 rounded transition"
            data-testid="manager-chat-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search input */}
        {showSearch && (
          <div className="px-4 py-2 border-b border-border bg-muted/20">
            <input
              type="text"
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search messages…"
              className="w-full bg-background border border-border rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-primary"
              data-testid="manager-chat-search-input"
            />
            {searchQuery && (
              <p className="text-[10px] text-muted-foreground mt-1">
                {messages.filter((m) => m.body?.toLowerCase().includes(searchQuery.toLowerCase())).length} match(es)
              </p>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-background/40">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="animate-spin text-muted-foreground" size={24} />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
              <MessageSquare size={32} className="mb-3 opacity-50" />
              <p className="text-sm">No messages yet</p>
              <p className="text-xs mt-1">Send the first one below.</p>
            </div>
          ) : (
            messages
              .filter((m) => !searchQuery || (m.body || '').toLowerCase().includes(searchQuery.toLowerCase()))
              .map((m) => {
                const isMine = m.sender_role === 'manager';
                // highlight matched portion
                let rendered = m.body;
                if (searchQuery && (m.body || '').toLowerCase().includes(searchQuery.toLowerCase())) {
                  const idx = m.body.toLowerCase().indexOf(searchQuery.toLowerCase());
                  rendered = (
                    <>
                      {m.body.slice(0, idx)}
                      <mark className="bg-amber-400/40 text-foreground rounded px-0.5">
                        {m.body.slice(idx, idx + searchQuery.length)}
                      </mark>
                      {m.body.slice(idx + searchQuery.length)}
                    </>
                  );
                }
                return (
                  <div
                    key={m.id}
                    className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                    data-testid={`chat-msg-${m.id}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                        isMine
                          ? 'bg-primary text-black rounded-br-sm'
                          : 'bg-muted text-foreground rounded-bl-sm'
                      }`}
                    >
                      {!isMine && (
                        <p className="text-[10px] uppercase tracking-wider opacity-70 mb-1">
                          {m.sender_name || 'Parent'}
                        </p>
                      )}
                      <p>{rendered}</p>
                      <p className={`flex items-center gap-1 text-[10px] mt-1 ${isMine ? 'justify-end opacity-70' : 'text-muted-foreground'}`}>
                        <span>{m.created_at ? format(parseISO(m.created_at), 'MMM d • HH:mm') : ''}</span>
                        {isMine && (
                          m.read_by_parent
                            ? <CheckCheck size={11} title="Read" />
                            : <Check size={11} title="Sent" />
                        )}
                      </p>
                    </div>
                  </div>
                );
              })
          )}
          {parentTyping && (
            <div className="flex justify-start" data-testid="parent-typing-indicator">
              <div className="bg-muted rounded-lg px-3 py-2 text-xs text-muted-foreground inline-flex items-center gap-2">
                <span className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-primary/70 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-primary/70 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-primary/70 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
                {parent?.name?.split(' ')[0] || 'Parent'} is typing…
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Composer */}
        <form onSubmit={handleSend} className="p-3 border-t border-border bg-card">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                pingTyping();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              rows={2}
              placeholder={`Message ${parent?.name?.split(' ')[0] || 'parent'}…`}
              className="flex-1 resize-none bg-background border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-primary"
              data-testid="manager-chat-input"
            />
            <button
              type="submit"
              disabled={!draft.trim() || sending}
              className="bg-primary text-black px-3 py-2 rounded-sm disabled:opacity-50 hover:bg-primary/90 transition"
              data-testid="manager-chat-send"
            >
              {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5">
            Press Enter to send • Shift+Enter for a new line
          </p>
        </form>
      </div>
    </div>
  );
};

export default ManagerChatDrawer;
